import React, { useState, useRef, useEffect } from 'react';
import type { BaseComponentProps } from '@/types';

interface MessageInputProps extends BaseComponentProps {
  onSendMessage: (content: string) => Promise<void>;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}

const MessageInput: React.FC<MessageInputProps> = ({ 
  onSendMessage,
  onTypingStart,
  onTypingStop,
  isLoading = false,
  disabled = false,
  placeholder = "Type a message...",
  maxLength = 1000,
  className = '' 
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120; // Max height in pixels
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  // Typing indicator logic
  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      onTypingStart?.();
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      onTypingStop?.();
    }, 2000);
  };

  const stopTyping = () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTyping) {
      setIsTyping(false);
      onTypingStop?.();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    if (value.length <= maxLength) {
      setMessage(value);
      if (value.trim()) {
        handleTyping();
      } else {
        stopTyping();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isLoading || disabled) return;

    try {
      stopTyping();
      const messageToSend = trimmedMessage;
      setMessage(''); // Clear input immediately for better UX
      
      await onSendMessage(messageToSend);
    } catch (error) {
      // Restore message if sending failed
      setMessage(trimmedMessage);
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const canSend = message.trim().length > 0 && !isLoading && !disabled;

  return (
    <div className={`bg-white border-t border-gray-200 p-4 ${className}`}>
      <form onSubmit={handleSubmit} className="flex items-end space-x-3">
        {/* Message Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              w-full resize-none rounded-lg border border-gray-300 px-3 py-2 pr-10
              focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm leading-relaxed
            `}
            style={{ 
              minHeight: '40px',
              maxHeight: '120px',
            }}
          />
          
          {/* Character Counter */}
          {maxLength && message.length > maxLength * 0.8 && (
            <div className={`
              absolute bottom-2 right-2 text-xs
              ${message.length >= maxLength ? 'text-red-500' : 'text-gray-400'}
            `}>
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!canSend}
          className={`
            flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
            transition-all duration-150
            ${canSend 
              ? 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white' 
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
          title={canSend ? 'Send message' : 'Type a message to send'}
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
          ) : (
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" 
              />
            </svg>
          )}
        </button>
      </form>

      {/* Typing Indicator */}
      {isTyping && (
        <div className="mt-2 text-xs text-gray-400 italic">
          Typing...
        </div>
      )}
    </div>
  );
};

export default MessageInput;